import { PDFDocument } from 'pdf-lib';
import { apiFetch } from './api';
import { safeFileName } from './safeFileName';
import { DOC_HTML_MESSAGE, type DocHtmlMessage } from './useDocEditor';
import type { SavedDocument } from '../api/schemas';

/**
 * Práce s více uloženými doklady najednou — přílohy faktury (soupis, ZP,
 * předávací protokol) a jejich sloučení do jednoho PDF.
 */

/** Blob → base64 (bez prefixu data:). */
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(new Error('Soubor se nepodařilo načíst.'));
    r.readAsDataURL(blob);
  });
}

/**
 * Vykreslí uložený formulář ve skrytém iframe (`?id=…&pdf=html`) a vrátí jeho
 * HTML — stejný výstup, jaký posílá tlačítko „Odeslat e-mailem" ve formuláři.
 * PDF z něj pak vygeneruje server.
 */
export function renderDocHtml(url: string, id: number, timeoutMs = 45_000): Promise<string> {
  return new Promise((resolve, reject) => {
    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.tabIndex = -1;
    // Mimo obrazovku, ale s reálnou šířkou A4 layoutu (display:none by nic nevykreslil).
    frame.style.cssText =
      'position:fixed;left:-12000px;top:0;width:1200px;height:1600px;border:0;opacity:0;pointer-events:none;';
    let timer = 0;
    const cleanup = () => {
      window.clearTimeout(timer);
      window.removeEventListener('message', onMsg);
      frame.remove();
    };
    const onMsg = (e: MessageEvent) => {
      if (e.origin !== window.location.origin || e.source !== frame.contentWindow) return;
      const m = e.data as DocHtmlMessage | undefined;
      if (!m || m.type !== DOC_HTML_MESSAGE || m.id !== id) return;
      cleanup();
      if (m.ok) resolve(m.html);
      else reject(new Error(m.error));
    };
    window.addEventListener('message', onMsg);
    timer = window.setTimeout(() => {
      cleanup();
      reject(new Error('Dokument se nepodařilo připravit (vypršel čas). Zkuste ho otevřít a odeslat z formuláře.'));
    }, timeoutMs);
    frame.src = url;
    document.body.appendChild(frame);
  });
}

/** HTML dokumentu → PDF přes backend (stejně jako tlačítko „PDF"). */
export async function htmlToPdfBlob(html: string, filename: string): Promise<Blob> {
  const landscape = /data-doc-landscape/.test(html);
  return (await apiFetch('/pdf/generate', {
    method: 'POST',
    body: { html, filename: safeFileName(filename), landscape },
  })) as Blob;
}

/** Uložený doklad z archivu → PDF (vykreslí ho skrytě ve formuláři). */
export async function savedDocPdf(doc: SavedDocument, path: string): Promise<{ blob: Blob; filename: string }> {
  const html = await renderDocHtml(`${path}?id=${doc.id}&pdf=html`, doc.id);
  const filename = safeFileName(doc.title || doc.doc_type);
  return { blob: await htmlToPdfBlob(html, filename), filename };
}

/** Sloučí PDF za sebou do jednoho. */
export async function mergePdfs(blobs: Blob[]): Promise<Blob> {
  const out = await PDFDocument.create();
  for (const b of blobs) {
    const src = await PDFDocument.load(await b.arrayBuffer(), { ignoreEncryption: true });
    const pages = await out.copyPages(src, src.getPageIndices());
    pages.forEach((p) => out.addPage(p));
  }
  const bytes = await out.save();
  return new Blob([bytes as Uint8Array<ArrayBuffer>], { type: 'application/pdf' });
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 4000);
}
